import i18n from "@/i18n";
/**
 * ChangeRestaurantDialog — INTERNAL TRANSFER. Same company only: the
 * employee's main restaurant changes from a date via the existing
 * Employment Terms revision endpoint. Employment, tenure and balances
 * stay the same. Backend checks scope over both restaurants.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { termsRevisionErrorMessage } from "./workforceHelpers";

const AVG_CODE = "TRANSFER_AVERAGING_SETTLEMENT_UNSUPPORTED";

export function buildTransferPayload({ destination, effectiveFrom, ack }) {
    return {
        effective_from: effectiveFrom, main_restaurant_id: destination,
        ...(ack ? { acknowledge_averaging_unsupported: true } : {}),
    };
}

export default function ChangeRestaurantDialog({
    open, onOpenChange, employeeId, restaurantId, restaurants, onSaved,
}) {
    const [destination, setDestination] = useState("");
    const [effectiveFrom, setEffectiveFrom] = useState("");
    const [cycles, setCycles] = useState(null);
    const [ack, setAck] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setDestination(""); setEffectiveFrom(""); setCycles(null);
            setAck(false); setError("");
        }
    }, [open]);

    const reset = (fn) => (v) => { fn(v); setCycles(null); setAck(false); setError(""); };
    const options = (restaurants || []).filter((r) => r.id !== restaurantId);
    const canSave = !pending && !!destination && !!effectiveFrom && (!cycles || ack);

    const save = async () => {
        if (!canSave) return;
        setPending(true);
        setError("");
        try {
            await api.post(
                `/workforce/employees/${employeeId}/employment-terms`,
                buildTransferPayload({ destination, effectiveFrom, ack: !!cycles && ack }),
                { params: { restaurant_id: restaurantId } });
            toast.success(i18n.t("wf.changeRestaurantDialog.mainRestaurantChanged", "Main restaurant changed"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.code === AVG_CODE) setCycles(detail.cycles || []);
            else setError(termsRevisionErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="transfer-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{i18n.t("wf.changeRestaurantDialog.changeRestaurant", "Change restaurant")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("wf.changeRestaurantDialog.moveTheEmployeeToAnotherRestaurant", "Move the employee to another restaurant of the company. Employment, start date and balances stay the same.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.changeRestaurantDialog.newMainRestaurant", "New main restaurant")}</span>
                        <Select value={destination} onValueChange={reset(setDestination)}>
                            <SelectTrigger className="mt-1" data-testid="transfer-destination">
                                <SelectValue placeholder={i18n.t("wf.changeRestaurantDialog.select", "Select…")} />
                            </SelectTrigger>
                            <SelectContent>
                                {options.map((r) => (
                                    <SelectItem key={r.id} value={r.id}
                                        data-testid={`transfer-opt-${r.id}`}>{r.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </label>
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.changeRestaurantDialog.effectiveFrom", "Effective from")}</span>
                        <Input type="date" className="mt-1" data-testid="transfer-effective-from"
                            value={effectiveFrom}
                            onChange={(e) => reset(setEffectiveFrom)(e.target.value)} />
                        <span className="mt-1.5 block text-xs text-neutral-500">
                            {i18n.t("wf.changeRestaurantDialog.mustBeTheStartOfA", "Must be the start of a Workforce period at the current restaurant.")}
                        </span>
                    </label>
                    {cycles && (
                        <div data-testid="transfer-averaging-warning"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 space-y-2">
                            <p>{i18n.t("wf.changeRestaurantDialog.openAveragingCycle", "An open Averaging cycle continues across this date")}
                                {cycles.map((c) => ` (${c.start_date} – ${c.end_date})`).join(",")}.
                                {" "}{i18n.t("wf.changeRestaurantDialog.miseoneCannotSettleThisCycleAutomatically", "MiseOne cannot settle this cycle automatically after a restaurant change; it will need manual handling.")}</p>
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={ack} data-testid="transfer-averaging-ack"
                                    onChange={(e) => setAck(e.target.checked)} />
                                {i18n.t("wf.changeRestaurantDialog.iUnderstandAndWantToContinue", "I understand and want to continue")}
                            </label>
                        </div>
                    )}
                    {error && (
                        <div role="alert" data-testid="transfer-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        data-testid="transfer-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                    <Button onClick={save} disabled={!canSave} data-testid="transfer-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("wf.changeRestaurantDialog.changeRestaurant", "Change restaurant")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
